import React, {useState, useEffect, useMemo, useCallback} from 'react';
import PropTypes from 'prop-types';

// @mui
import {styled, alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Alert from '@mui/material/Alert';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';

// icons
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PersonIcon from '@mui/icons-material/Person';

// components
import ServiceToggleButton from '../components/ServiceToggleButton';

// constants
import {SERVICE_TYPES, ERROR_MESSAGES} from '../constants/reservation';

// ----------------------------------------------------------------------

const CounterContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  width: '100%',
}));

const CounterButton = styled(IconButton)(({theme}) => ({
  'backgroundColor': alpha(theme.palette.primary.main, 0.08),
  'color': theme.palette.primary.main,
  'margin': theme.spacing(0, 2),
  'transition': theme.transitions.create(['transform', 'box-shadow']),
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
    backgroundColor: alpha(theme.palette.primary.main, 0.15),
  },
  '&.Mui-disabled': {
    color: theme.palette.text.disabled,
    backgroundColor: alpha(theme.palette.divider, 0.3),
  },
}));

const CounterTextField = styled(TextField)(({theme}) => ({
  '& .MuiInputBase-input': {
    textAlign: 'center',
    width: '100%',
    padding: theme.spacing(1),
    fontSize: '1.12rem',
    fontWeight: 'bold',
  },
  '& .MuiOutlinedInput-root': {
    width: '90px',
  },
  '& .MuiInputAdornment-root': {
    '& .MuiSvgIcon-root': {
      color: theme.palette.text.secondary,
    },
  },
}));

// ----------------------------------------------------------------------

export function ServiceGuestsStep({
  formData,
  updateFormData,
  config,
  setValidation,
  setErrors,
  errors,
  setStepReady, // Add this prop
}) {
  // État local pour permettre une mise à jour immédiate de l'UI
  const [localPartySize, setLocalPartySize] = useState(formData.partySize);

  // Get constraints from config
  const constraints = useMemo(() => {
    if (!config) {
      return {
        minGuests: 1,
        maxGuests: 10,
        message: 'Pour les groupes plus nombreux, veuillez nous contacter par téléphone.',
      };
    }

    return {
      minGuests: config.reservationConstraints?.minGuests || 1,
      maxGuests: config.reservationConstraints?.onlineBooking?.maxGuests || 10,
      message: config.reservationConstraints?.onlineBooking?.message ||
        'Pour les groupes plus nombreux, veuillez nous contacter par téléphone.',
    };
  }, [config]);

  // Fonction de validation combinée pour cette étape
  const validateStep = useCallback(() => {
    // Validation du service
    if (!formData.service) {
      setErrors({service: ERROR_MESSAGES.SERVICE_REQUIRED});
      return false;
    }

    // Validation du nombre de personnes
    if (localPartySize < constraints.minGuests) {
      // eslint-disable-next-line new-cap
      setErrors({partySize: ERROR_MESSAGES.PARTY_SIZE_MIN(constraints.minGuests)});
      return false;
    }

    if (localPartySize > constraints.maxGuests) {
      // eslint-disable-next-line new-cap
      setErrors({partySize: ERROR_MESSAGES.PARTY_SIZE_MAX(constraints.maxGuests)});
      return false;
    }

    return true;
  }, [formData.service, localPartySize, constraints.minGuests, constraints.maxGuests, setErrors]);

  // Enregistrer la fonction de validation auprès du parent
  useEffect(() => {
    setValidation(validateStep);
  }, [setValidation, validateStep, formData.service, localPartySize, constraints.minGuests, constraints.maxGuests]);

  // Effect to control next button state
  useEffect(() => {
    if (setStepReady) {
      // Enable button only when service is selected and party size is valid
      const isServiceSelected = !!formData.service;
      const isPartySizeValid = localPartySize >= constraints.minGuests && localPartySize <= constraints.maxGuests;

      setStepReady(isServiceSelected && isPartySizeValid);
    }
  }, [formData.service, localPartySize, constraints.minGuests, constraints.maxGuests, setStepReady]);

  // Gestion du service
  const handleServiceSelection = (_, serviceType) => {
    if (serviceType !== null) {
      updateFormData('service', serviceType);
      // Si une erreur existe, l'effacer car l'utilisateur a fait une sélection
      if (errors.service) {
        setErrors({});
      }
    }
  };

  // Get service schedule from config
  const getServiceSchedule = (serviceType) => {
    if (!config || !config.serviceSchedule) return null;

    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

    for (const day of days) {
      const services = config.serviceSchedule[day] || [];
      const service = services.find((s) => s.name === serviceType && s.active);

      if (service) {
        return {
          startTime: service.start,
          endTime: service.end,
        };
      }
    }

    return null;
  };

  const lunchSchedule = getServiceSchedule(SERVICE_TYPES.LUNCH);
  const dinnerSchedule = getServiceSchedule(SERVICE_TYPES.DINNER);

  // Gestion des personnes
  const handleIncrement = () => {
    if (localPartySize < constraints.maxGuests) {
      const newValue = localPartySize + 1;
      setLocalPartySize(newValue);
      // Immediately update formData as well
      updateFormData('partySize', newValue);

      // Effacer les erreurs si elles existent
      if (errors.partySize) {
        setErrors({});
      }
    }
  };

  // Gérer la décrémentation
  const handleDecrement = () => {
    if (localPartySize > 0) {
      const newValue = localPartySize - 1;
      setLocalPartySize(newValue);
      // Immediately update formData as well
      updateFormData('partySize', newValue);

      // Effacer les erreurs si elles existent
      if (errors.partySize) {
        setErrors({});
      }
    }
  };

  // Gérer la modification directe du champ
  const handleInputChange = (event) => {
    const value = parseInt(event.target.value, 10);

    if (isNaN(value)) {
      setLocalPartySize(0);
      updateFormData('partySize', 0);
      return;
    }

    // Limiter la valeur entre 0 et maxGuests
    const newValue = Math.min(Math.max(0, value), constraints.maxGuests);
    setLocalPartySize(newValue);
    // Immediately update formData as well
    updateFormData('partySize', newValue);

    // Effacer les erreurs si elles existent
    if (errors.partySize) {
      setErrors({});
    }
  };

  const showMaxGuestsMessage = localPartySize >= constraints.maxGuests;

  return (
    <Fade in timeout={300}>
      <Box>
        {/* Section Service */}
        <Typography variant="h6" sx={{mb: 2, textAlign: 'center'}}>
          Quel service souhaitez-vous réserver ?
        </Typography>

        <ToggleButtonGroup
          value={formData.service}
          exclusive
          onChange={handleServiceSelection}
          aria-label="service selection"
          fullWidth
          sx={{
            'border': 'none',
            'mb': 3,
          }}
        >
          <ServiceToggleButton
            value={SERVICE_TYPES.LUNCH}
            serviceType={SERVICE_TYPES.LUNCH}
            aria-label="déjeuner"
            disabled={!lunchSchedule}
          >
            <Typography variant="body2">Midi</Typography>
            {lunchSchedule && (
              <Typography variant="caption" color="text.secondary">
                {lunchSchedule.startTime} - {lunchSchedule.endTime}
              </Typography>
            )}
          </ServiceToggleButton>

          <ServiceToggleButton
            value={SERVICE_TYPES.DINNER}
            serviceType={SERVICE_TYPES.DINNER}
            aria-label="dîner"
            disabled={!dinnerSchedule}
          >
            <Typography variant="body2">Soir</Typography>
            {dinnerSchedule && (
              <Typography variant="caption" color="text.secondary">
                {dinnerSchedule.startTime} - {dinnerSchedule.endTime}
              </Typography>
            )}
          </ServiceToggleButton>
        </ToggleButtonGroup>

        {errors.service && (
          <Alert severity="error" sx={{mb: 2}}>
            {errors.service}
          </Alert>
        )}

        {/* Section Guests */}
        <Typography variant="body1" sx={{mb: 2, textAlign: 'center', fontWeight: 'medium'}}>
          Pour combien de personnes ?
        </Typography>

        <CounterContainer>
          <CounterButton
            onClick={handleDecrement}
            disabled={localPartySize <= 0}
            size="medium"
          >
            <RemoveIcon />
          </CounterButton>

          <CounterTextField
            value={localPartySize}
            onChange={handleInputChange}
            variant="outlined"
            InputProps={{
              readOnly: false,
              endAdornment: (
                <InputAdornment position="end">
                  <PersonIcon fontSize="medium" />
                </InputAdornment>
              ),
            }}
            inputProps={{
              'min': 0,
              'max': constraints.maxGuests,
              'aria-label': 'Nombre de personnes',
              'inputMode': 'numeric',
              'pattern': '[0-9]*',
            }}
          />

          <CounterButton
            onClick={handleIncrement}
            disabled={localPartySize >= constraints.maxGuests}
            size="medium"
          >
            <AddIcon />
          </CounterButton>
        </CounterContainer>

        {showMaxGuestsMessage && (
          <Alert
            severity="info"
            icon={<InfoIcon />}
            sx={{mb: 2}}
            variant="outlined"
          >
            {constraints.message}
          </Alert>
        )}

        {errors.partySize && (
          <Alert severity="error" sx={{mt: 2}}>
            {errors.partySize}
          </Alert>
        )}
      </Box>
    </Fade>
  );
}

ServiceGuestsStep.propTypes = {
  formData: PropTypes.object.isRequired,
  updateFormData: PropTypes.func.isRequired,
  config: PropTypes.object,
  setValidation: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setStepReady: PropTypes.func, // Add this to PropTypes
};
